import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Redirect, Route, Switch } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import RequireAuth from "./components/RequireAuth";
import RequireOperationsAccess from "./components/RequireOperationsAccess";
import { ThemeProvider } from "./contexts/ThemeContext";
import { lazy, Suspense } from "react";
import { PermissionGate } from "./components/PermissionGate";
// All pages lazy-loaded for optimal code splitting
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Home = lazy(() => import('./pages/Home'));
const JadePromotion = lazy(() => import('./pages/JadePromotion'));
const SophiaPromotion = lazy(() => import('./pages/SophiaPromotion'));
const Careers = lazy(() => import('./pages/Careers'));
const Welcome = lazy(() => import('./pages/Welcome'));
const Planner = lazy(() => import('./pages/Planner'));
const Admin = lazy(() => import('./pages/Admin'));
const MentoringGuide = lazy(() => import('./pages/MentoringGuide'));
const SupportLoop = lazy(() => import('./pages/SupportLoop'));
const DevelopmentPlan = lazy(() => import('./pages/DevelopmentPlan'));
const MaddyJourney = lazy(() => import('./pages/MaddyJourney'));
const EchoLanding = lazy(() => import('./pages/EchoLanding'));
const EchoCharacterBible = lazy(() => import('./pages/EchoCharacterBible'));
const EchoGardenStory = lazy(() => import('./pages/EchoGardenStory'));
const EchoTrickyGoodbye = lazy(() => import('./pages/EchoTrickyGoodbye'));
const EchoTrickyTower = lazy(() => import('./pages/EchoTrickyTower'));
const EchoBusyPark = lazy(() => import('./pages/EchoBusyPark'));
const EchoNewPlace = lazy(() => import('./pages/EchoNewPlace'));
const EchoBiggerGame = lazy(() => import('./pages/EchoBiggerGame'));
const AssistWebsite = lazy(() => import('./pages/AssistWebsite'));
const NdisReviewDashboard = lazy(() => import('./pages/NdisReviewDashboard'));
const ActionCentre = lazy(() => import('./pages/ActionCentre'));
const AdminEnquiries = lazy(() => import('./pages/AdminEnquiries'));
const Reports = lazy(() => import('./pages/Reports'));
const ReportPrep = lazy(() => import('./pages/ReportPrep'));
const ReminderSettings = lazy(() => import('./pages/ReminderSettings'));
const FCAList = lazy(() => import('./pages/FCAList'));
const Certificates = lazy(() => import('./pages/Certificates'));
const TeamAccess = lazy(() => import('./pages/TeamAccess'));
const FCABuilder = lazy(() => import('./pages/FCABuilder'));
const DEvsOT = lazy(() => import('./pages/DEvsOT'));
const SchoolBasedSupport = lazy(() => import('./pages/SchoolBasedSupport'));
const BigEmotions = lazy(() => import('./pages/BigEmotions'));
const SocialSkillsAutism = lazy(() => import('./pages/SocialSkillsAutism'));
const DevTherapyAdelaide = lazy(() => import('./pages/DevTherapyAdelaide'));
const PlanManagers = lazy(() => import('./pages/PlanManagers'));
const FCA = lazy(() => import('./pages/FCA'));
const HowWeWork = lazy(() => import('./pages/HowWeWork'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const GetStarted = lazy(() => import('./pages/GetStarted'));
const GetStartedOT = lazy(() => import('./pages/GetStartedOT'));
const NDISDevelopmentalEducator = lazy(() => import('./pages/NDISDevelopmentalEducator'));
const AutismTherapyAdelaide = lazy(() => import('./pages/AutismTherapyAdelaide'));
const EmotionalRegulationTherapy = lazy(() => import('./pages/EmotionalRegulationTherapy'));
const EchoProject = lazy(() => import('./pages/EchoProject'));
const EchoProjectRobert = lazy(() => import('./pages/EchoProjectRobert'));
const SACalculator = lazy(() => import('./pages/SACalculator'));
const OperationsHub = lazy(() => import('./pages/OperationsHub'));
const OperationsIntake = lazy(() => import('./pages/OperationsIntake'));
const OperationsAgreements = lazy(() => import('./pages/OperationsAgreements'));
const OperationsImport = lazy(() => import('./pages/OperationsImport'));
const TherapistParentCallTemplate = lazy(() => import('./pages/TherapistParentCallTemplate'));
const HomepageTrial = lazy(() => import('./pages/HomepageTrial'));
const EchoStorybooks = lazy(() => import('./pages/EchoStorybooks'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Minimal loading fallback for lazy pages
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="w-8 h-8 border-2 border-[#0a1628] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

/** Helper to wrap a component in auth guard */
function Private({ children }: { children: React.ReactNode }) {
  return <RequireAuth>{children}</RequireAuth>;
}

function Router() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        {/* ===== PRIVATE ROUTES (require login) ===== */}
        <Route path="/dashboard">
          {() => <Private><Dashboard /></Private>}
        </Route>
        <Route path="/emma">
          {() => <Private><PermissionGate permission="team_documents"><Home /></PermissionGate></Private>}
        </Route>
        <Route path="/maddy">
          {() => <Private><PermissionGate permission="team_documents"><MaddyJourney /></PermissionGate></Private>}
        </Route>
        <Route path="/jade">
          {() => <Private><PermissionGate permission="team_documents"><JadePromotion /></PermissionGate></Private>}
        </Route>
        <Route path="/sophia">
          {() => <Private><PermissionGate permission="team_documents"><SophiaPromotion /></PermissionGate></Private>}
        </Route>
        <Route path="/admin/enquiries">
          {() => <Private><PermissionGate permission="enquiries"><AdminEnquiries /></PermissionGate></Private>}
        </Route>
        <Route path="/admin">
          {() => <Private><PermissionGate permission="client_directory"><Admin /></PermissionGate></Private>}
        </Route>
        <Route path="/ndis-reviews">
          {() => <Private><PermissionGate permission="ndis_reviews"><NdisReviewDashboard /></PermissionGate></Private>}
        </Route>
        <Route path="/action-centre">
          {() => <Private><PermissionGate permission="action_centre"><ActionCentre /></PermissionGate></Private>}
        </Route>
        <Route path="/reports/prep/:reportId">
          {() => <Private><PermissionGate permission="reports"><ReportPrep /></PermissionGate></Private>}
        </Route>
        <Route path="/reports">
          {() => <Private><PermissionGate permission="reports"><Reports /></PermissionGate></Private>}
        </Route>
        <Route path="/reminder-settings">
          {() => <Private><ReminderSettings /></Private>}
        </Route>
        <Route path="/fca-list">
          {() => <Private><PermissionGate permission="fca_builder"><FCAList /></PermissionGate></Private>}
        </Route>
        <Route path="/certificates">
          {() => <Private><RequireOperationsAccess><Certificates /></RequireOperationsAccess></Private>}
        </Route>
        <Route path="/team-access">
          {() => <Private><PermissionGate permission="team_access_admin"><TeamAccess /></PermissionGate></Private>}
        </Route>
        <Route path="/fca-builder/new">
          {() => <Private><PermissionGate permission="fca_builder"><FCABuilder /></PermissionGate></Private>}
        </Route>
        <Route path="/fca-builder/:id">
          {() => <Private><PermissionGate permission="fca_builder"><FCABuilder /></PermissionGate></Private>}
        </Route>
        <Route path="/mentoring-guide">
          {() => <Private><PermissionGate permission="team_documents"><MentoringGuide /></PermissionGate></Private>}
        </Route>
        <Route path="/support-loop">
          {() => <Private><PermissionGate permission="team_documents"><SupportLoop /></PermissionGate></Private>}
        </Route>
        <Route path="/development-plan">
          {() => <Private><PermissionGate permission="team_documents"><DevelopmentPlan /></PermissionGate></Private>}
        </Route>
        <Route path="/sa-calculator">
          {() => <Private><RequireOperationsAccess><SACalculator /></RequireOperationsAccess></Private>}
        </Route>
        <Route path="/operations">
          {() => <Private><RequireOperationsAccess><OperationsHub /></RequireOperationsAccess></Private>}
        </Route>
        <Route path="/operations/intake">
          {() => <Private><RequireOperationsAccess><OperationsIntake /></RequireOperationsAccess></Private>}
        </Route>
        <Route path="/operations/agreements">
          {() => <Private><RequireOperationsAccess><OperationsAgreements /></RequireOperationsAccess></Private>}
        </Route>
        <Route path="/operations/import">
          {() => <Private><RequireOperationsAccess><OperationsImport /></RequireOperationsAccess></Private>}
        </Route>
        <Route path="/therapist-parent-call">
          {() => <Private><TherapistParentCallTemplate /></Private>}
        </Route>
        <Route path="/internal/homepage-trial">
          {() => <Private><HomepageTrial /></Private>}
        </Route>
        <Route path="/internal/echo-storybooks">
          {() => <Private><EchoStorybooks /></Private>}
        </Route>
        <Route path="/echo-project">
          {() => <Private><EchoProject /></Private>}
        </Route>
        <Route path="/echo-project-robert">
          {() => <Private><EchoProjectRobert /></Private>}
        </Route>

        {/* ===== PUBLIC ROUTES (anyone can view) ===== */}
        <Route path="/planner/:clientId?">
          {() => <Planner />}
        </Route>
        <Route path="/echo">
          {() => <EchoLanding />}
        </Route>
        <Route path="/echo/story/when-everything-feels-too-much">
          {() => <EchoBusyPark />}
        </Route>
        <Route path="/echo/story/starting-somewhere-new">
          {() => <EchoNewPlace />}
        </Route>
        <Route path="/echo/story/echo-finds-a-brave-voice">
          {() => <EchoBiggerGame />}
        </Route>
        <Route path="/echo/story/echo-and-the-garden-surprise">
          {() => <EchoGardenStory />}
        </Route>
        <Route path="/echo/story/echo-and-the-tricky-goodbye">
          {() => <EchoTrickyGoodbye />}
        </Route>
        <Route path="/echo/story/echo-and-the-tricky-tower">
          {() => <EchoTrickyTower />}
        </Route>
        <Route path="/echo/story/echo-and-the-busy-park">
          {() => <EchoBusyPark />}
        </Route>
        <Route path="/echo/story/echo-and-the-new-place">
          {() => <EchoNewPlace />}
        </Route>
        <Route path="/echo/story/echo-and-the-bigger-game">
          {() => <EchoBiggerGame />}
        </Route>
        <Route path="/echo/character-bible">
          {() => <EchoCharacterBible />}
        </Route>
        <Route path="/plan-managers">
          {() => <PlanManagers />}
        </Route>
        <Route path="/fca">
          {() => <FCA />}
        </Route>
        <Route path="/how-we-work">
          {() => <HowWeWork />}
        </Route>
        <Route path="/privacy">
          {() => <PrivacyPolicy />}
        </Route>
        <Route path="/get-started">
          {() => <GetStarted />}
        </Route>
        <Route path="/get-started-ot">
          {() => <GetStartedOT />}
        </Route>
        <Route path="/de-vs-ot">
          {() => <DEvsOT />}
        </Route>
        <Route path="/school-based-support">
          {() => <SchoolBasedSupport />}
        </Route>
        <Route path="/big-emotions">
          {() => <BigEmotions />}
        </Route>
        <Route path="/social-skills-autism">
          {() => <SocialSkillsAutism />}
        </Route>
        <Route path="/developmental-therapy-adelaide">
          {() => <DevTherapyAdelaide />}
        </Route>
        <Route path="/careers">
          {() => <Careers />}
        </Route>
        <Route path="/welcome">
          {() => <Welcome />}
        </Route>
        <Route path="/meetecho">
          {() => <Redirect to="/echo" />}
        </Route>

        <Route path="/ndis-developmental-educator-adelaide">
          {() => <NDISDevelopmentalEducator />}
        </Route>
        <Route path="/autism-therapy-adelaide">
          {() => <AutismTherapyAdelaide />}
        </Route>
        <Route path="/emotional-regulation-therapy-children">
          {() => <EmotionalRegulationTherapy />}
        </Route>

        {/* ===== PUBLIC HOMEPAGE (must be last before fallback) ===== */}
        <Route path="/">
          {() => <AssistWebsite />}
        </Route>

        {/* ===== FALLBACK ===== */}
        <Route path="/404">
          {() => <NotFound />}
        </Route>
        <Route>
          {() => <NotFound />}
        </Route>
      </Switch>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <TooltipProvider>
          <Toaster />
          <Router />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
